import store from '../redux/store';
import { AuthStateLoginStatus } from './auth';
import { isValidCoordinates, isWithinRanges } from '../utils/profile';
import { JobsiteUpdateOperationType } from './profile';
import { US_LATITUDE_RANGES, US_LONGITUDE_RANGES } from '../constants/profile';
import { validateUSPhoneNumber, ValidateUSZipCode } from '../utils/checkout';
import { z, ZodObject } from 'zod';

export const jobsiteDetailsFormSchema = z
  .object({
    jobsiteSelectionType: z
      .enum([JobsiteUpdateOperationType.ADD, JobsiteUpdateOperationType.SELECT])
      .nullish(),
    jobsiteLocationId: z.number().nullish(),
    saveNewJobsite: z.boolean().nullish(),
    email: z.string().min(1, 'Email is required').email('Invalid email address'),
    company: z.string(),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    mobile: z
      .string()
      .min(1, 'Mobile number is required')
      .refine(validateUSPhoneNumber, 'Invalid mobile number'),
    jobsiteName: z.string().nullable().optional(),
    latitudeLongitude: z
      .string()
      .nullable()
      .refine((value) => value !== null && value.trim() !== '', {
        message: 'Coordinates are required',
      })
      .refine(
        async (value) => {
          if (!value) return true;
          const coordinatesDetails = isValidCoordinates(value);

          if (!coordinatesDetails.valid) {
            return false;
          }

          const latitude = Number(coordinatesDetails.latitude);
          const longitude = Number(coordinatesDetails.longitude);

          if (
            !isWithinRanges(latitude, US_LATITUDE_RANGES) ||
            !isWithinRanges(longitude, US_LONGITUDE_RANGES)
          ) {
            return false;
          }

          return true;
        },
        {
          message: 'Invalid pair of US coordinates',
        },
      ),
    address: z
      .string()
      .min(1, 'Address is required')
      .max(256, 'Please limit your input to 256 characters.'),
    zip: z.string().min(1, 'Zip is required').refine(ValidateUSZipCode, 'Invalid zip code'),
    city: z
      .string()
      .min(1, 'City is required')
      .max(256, 'Please limit your input to 256 characters.'),
    state: z.string().min(1, 'State is required'),
  })
  .superRefine((val, ctx) => {
    const isLoggedIn = store.getState().auth?.loginStatus === AuthStateLoginStatus.AUTHENTICATED;

    if (isLoggedIn && val.jobsiteSelectionType !== JobsiteUpdateOperationType.SELECT) {
      if (!val.jobsiteName || val.jobsiteName.trim() === '') {
        ctx.addIssue({
          path: ['jobsiteName'],
          code: z.ZodIssueCode.custom,
          message: 'Jobsite name required',
        });
        return;
      }

      if (val.jobsiteName.length > 128) {
        ctx.addIssue({
          path: ['jobsiteName'],
          code: z.ZodIssueCode.custom,
          message: 'Please limit your input to 128 characters',
        });
        return;
      }
    }
  });

export const deliveryDetailsFormSchema = z.object({
  modeOfDelivery: z.string().min(1, 'Mode of Delivery is required'),
  modeOfReturning: z.string().min(1, 'Mode of Returning is required'),
  whoWillReceive: z.string().min(1, 'Who will receive is required'),
  specialRequests_DeliveryInstructions: z.string(),
  contactName: z.string().min(1, 'Contact Name is required'),
  mobile: z
    .string()
    .min(1, 'Mobile number is required')
    .refine(validateUSPhoneNumber, 'Invalid mobile number'),
  purchaseOrder: z.string().max(100, 'Please limit your input to 100 characters.').nullable(),
});

export const billingDetailsFormSchema = z.object({
  isJobsiteAndBillingAddressSame: z.boolean().default(true),
  contactName: z.string().min(1, 'Name is required'),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  address: z.string().min(1, 'Address is required'),
  zip: z.string().min(1, 'Zip is required').refine(ValidateUSZipCode, 'Invalid zip code'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
});

export const checkoutFormSchema: CheckoutFormSchemaType = z.object({
  jobsiteDetails: jobsiteDetailsFormSchema,
  deliveryDetails: deliveryDetailsFormSchema,
  billingDetails: billingDetailsFormSchema,
});

export type CheckoutFormModel = z.infer<typeof checkoutFormSchema>;
export type JobsiteFormModel = z.infer<typeof jobsiteDetailsFormSchema>;
export type DeliveryFormModel = z.infer<typeof deliveryDetailsFormSchema>;
export type BillingFormModel = z.infer<typeof billingDetailsFormSchema>;

export type CheckoutFormSchemaType = ZodObject<{
  [key in CheckoutFormKey]: CheckoutSubFormSchemaType;
}>;

export type CheckoutSubFormSchemaType =
  | typeof jobsiteDetailsFormSchema
  | typeof deliveryDetailsFormSchema
  | typeof billingDetailsFormSchema;

export type FieldName = keyof CheckoutFormModel;

export type CheckoutFormSubVariant = JobsiteFormModel | DeliveryFormModel | BillingFormModel;

export type CheckoutFormKey = 'jobsiteDetails' | 'deliveryDetails' | 'billingDetails';
